import { Clock, Mail, MapPin, MessageCircle, Phone, FileText, ArrowUpRight } from "lucide-react";
import { Seo } from "../lib/seo";
import { SITE, telLink, mailLink, whatsappLink } from "../lib/site";
import { PageHero } from "../components/PageHero";
import { EnquiryForm } from "../components/EnquiryForm";
import { Reveal } from "../components/motion";

export const Contact = () => (
  <>
    <Seo
      title="Contact & Legal Enquiry"
      description="Submit a legal enquiry to Upgrade Law — corporate, trademark, contract, property, compliance and dispute matters across India. Call, WhatsApp or email the practice."
      path="/contact"
    />
    <PageHero
      eyebrow="Contact & Legal Enquiry"
      title={["Tell us what you are", "facing. Briefly is fine."]}
      description="Share a short description of your legal need and the best way to reach you. Please do not send confidential documents through this form."
    />

    <section className="bg-white py-24 sm:py-28">
      <div className="container-x grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal>
            <div className="border border-line bg-canvas p-8 sm:p-12">
              <p className="eyebrow">The enquiry form</p>
              <h2 className="mt-4 font-serif text-3xl text-ink">Start the conversation.</h2>
              <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-steel">
                <FileText size={14} className="mt-0.5 shrink-0 text-gold-dark" />
                This form is for initial enquiries only. Do not share case files,
                contracts, ID documents or other confidential material.
              </p>
              <div className="mt-9">
                <EnquiryForm />
              </div>
            </div>
          </Reveal>
        </div>

        <aside className="space-y-5 lg:col-span-5">
          <Reveal delay={0.1}>
            <a
              href={telLink}
              data-testid="contact-call-card"
              className="group flex items-start gap-5 border border-line p-7 transition-all duration-500 hover:border-gold/60 hover:bg-canvas"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-navy text-gold transition-colors group-hover:bg-gold group-hover:text-navy">
                <Phone size={18} />
              </span>
              <span>
                <span className="eyebrow block">Click to call</span>
                <span className="mt-2 block font-serif text-2xl text-ink">{SITE.phoneDisplay}</span>
                <span className="mt-1 block text-xs text-steel">Mon–Sat, during business hours</span>
              </span>
            </a>
          </Reveal>
          <Reveal delay={0.16}>
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-card"
              className="group flex items-start gap-5 border border-line p-7 transition-all duration-500 hover:border-gold/60 hover:bg-canvas"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-navy text-gold transition-colors group-hover:bg-gold group-hover:text-navy">
                <MessageCircle size={18} />
              </span>
              <span>
                <span className="eyebrow block">WhatsApp</span>
                <span className="mt-2 block font-serif text-2xl text-ink">Message the practice</span>
                <span className="mt-1 block text-xs text-steel">Opens WhatsApp with a pre-filled enquiry note</span>
              </span>
            </a>
          </Reveal>
          <Reveal delay={0.22}>
            <a
              href={mailLink}
              data-testid="contact-email-card"
              className="group flex items-start gap-5 border border-line p-7 transition-all duration-500 hover:border-gold/60 hover:bg-canvas"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-navy text-gold transition-colors group-hover:bg-gold group-hover:text-navy">
                <Mail size={18} />
              </span>
              <span>
                <span className="eyebrow block">Email</span>
                <span className="mt-2 block font-serif text-xl text-ink">{SITE.email}</span>
                <span className="mt-1 block text-xs text-steel">No attachments are needed at this stage</span>
              </span>
            </a>
          </Reveal>
          <Reveal delay={0.28}>
            <div className="space-y-5" data-testid="contact-address-card">
              {SITE.offices.map((office) => (
                <div key={office.label} className="border border-line p-7">
                  <span className="eyebrow flex items-center gap-2">
                    <MapPin size={13} className="text-gold-dark" /> {office.label}
                  </span>
                  <p className="mt-3 text-sm leading-relaxed text-steel">
                    {office.lines.map((l) => (
                      <span key={l} className="block">
                        {l}
                      </span>
                    ))}
                  </p>
                  <iframe
                    title={`Map — ${office.label}`}
                    src={SITE.mapEmbed(office.query)}
                    className="mt-5 h-44 w-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                  <a
                    href={SITE.directions(office.query)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`office-directions-${office.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-navy transition-colors hover:text-gold-dark"
                  >
                    Get directions <ArrowUpRight size={13} />
                  </a>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.34}>
            <div className="border border-gold/40 bg-cream p-7">
              <p className="eyebrow">What happens next</p>
              <ol className="mt-4 space-y-3 text-sm leading-relaxed text-steel">
                {[
                  "Your enquiry is logged with a reference number.",
                  "We review the description and respond at the contact details you provided.",
                  "If the matter needs engagement, we propose scope and terms in writing.",
                ].map((s, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="font-mono text-xs text-gold-dark">0{i + 1}</span>
                    {s}
                  </li>
                ))}
              </ol>
            </div>
          </Reveal>
        </aside>
      </div>

      <div className="container-x mt-14">
        <Reveal>
          <p className="border-l-2 border-gold pl-5 text-xs leading-relaxed text-steel/80">
            {SITE.placeholderNotice} Viewing this page does not create an
            advocate-client relationship — see the{" "}
            <a href="/disclaimer" className="gold-underline font-medium text-ink">
              Disclaimer
            </a>
            .
          </p>
        </Reveal>
      </div>
    </section>
  </>
);

export default Contact;
